import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fetchInsights } from '../lib/api';
import { formatPrice, formatDateShort } from '../lib/format';
import type { InsightsData, InsightsCategory } from '../lib/types';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';
import usePageTitle from '../hooks/usePageTitle';
import {
  ChartBarIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  MinusCircleIcon,
  ArrowTrendingDownIcon,
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowsUpDownIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

type StatusFilter = 'all' | 'cheaper' | 'more_expensive' | 'same';
type SortKey = 'match' | 'category' | 'ticketed' | 'competitor' | 'diff' | 'pct';
type SortDir = 'asc' | 'desc';

export default function InsightsPage() {
  usePageTitle('Ticketed Insights');

  const [data, setData] = useState<InsightsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [leagueFilter, setLeagueFilter] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('diff');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    fetchInsights()
      .then(setData)
      .catch(() => setError('Failed to load insights'))
      .finally(() => setLoading(false));
  }, []);

  // Extract unique leagues
  const leagues = useMemo(() => {
    if (!data) return [];
    const seen = new Map<string, string>();
    for (const c of data.categories) {
      if (c.league_slug && c.league_name && !seen.has(c.league_slug)) {
        seen.set(c.league_slug, c.league_name);
      }
    }
    return Array.from(seen.entries()).map(([slug, name]) => ({ slug, name }));
  }, [data]);

  // Filter
  const filtered = useMemo(() => {
    if (!data) return [];
    let result = data.categories;
    if (statusFilter !== 'all') {
      result = result.filter((c) => c.status === statusFilter);
    }
    if (leagueFilter) {
      result = result.filter((c) => c.league_slug === leagueFilter);
    }
    return result;
  }, [data, statusFilter, leagueFilter]);

  // Sort
  const sorted = useMemo(() => {
    const list = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      switch (sortKey) {
        case 'match':
          return dir * (`${a.home_team} v ${a.away_team}`).localeCompare(`${b.home_team} v ${b.away_team}`);
        case 'category':
          return dir * a.group_label.localeCompare(b.group_label);
        case 'ticketed':
          return dir * (a.ticketed_price - b.ticketed_price);
        case 'competitor':
          return dir * (a.cheapest_competitor - b.cheapest_competitor);
        case 'diff':
          return dir * (Math.abs(a.diff) - Math.abs(b.diff));
        case 'pct':
          return dir * (Math.abs(a.pct) - Math.abs(b.pct));
        default:
          return 0;
      }
    });
    return list;
  }, [filtered, sortKey, sortDir]);

  const handleSort = useCallback((key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'match' || key === 'category' ? 'asc' : 'desc');
    }
  }, [sortKey]);

  // Biggest opportunities (most expensive vs competitor, sorted by absolute diff)
  const biggestOverpays = useMemo(() => {
    if (!data) return [];
    return data.categories
      .filter((c) => c.status === 'more_expensive')
      .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff))
      .slice(0, 5);
  }, [data]);

  // Biggest wins
  const biggestWins = useMemo(() => {
    if (!data) return [];
    return data.categories
      .filter((c) => c.status === 'cheaper')
      .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff))
      .slice(0, 5);
  }, [data]);

  if (loading) {
    return (
      <div>
        <div className="h-8 w-64 mb-8 rounded bg-slate-700/50 animate-pulse" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <EmptyState
        icon={<ChartBarIcon className="w-7 h-7" />}
        title="Insights unavailable"
        description={error || 'Something went wrong loading insights.'}
        actionLabel="Back to Fixtures"
        actionTo="/"
      />
    );
  }

  const { summary } = data;
  const activeFilters = (statusFilter !== 'all' ? 1 : 0) + (leagueFilter ? 1 : 0);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-2">
          Ticketed Insights
        </h1>
        <p className="text-slate-400">
          How Ticketed compares against competitors across all upcoming fixtures
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-8">
        <div className="card p-4 sm:p-5">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Win Rate</p>
          <p className="text-3xl font-bold text-emerald-400">{summary.win_rate}%</p>
          <p className="text-xs text-slate-500 mt-1">of {summary.total_categories} categories</p>
        </div>

        <button
          onClick={() => setStatusFilter(statusFilter === 'cheaper' ? 'all' : 'cheaper')}
          className={`card p-4 sm:p-5 text-left transition-all ${
            statusFilter === 'cheaper' ? 'ring-2 ring-emerald-500/50' : ''
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <CheckCircleIcon className="w-4 h-4 text-emerald-400" />
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Cheaper</p>
          </div>
          <p className="text-3xl font-bold text-emerald-400">{summary.cheaper}</p>
          <p className="text-xs text-emerald-400/70 mt-1">Saving {formatPrice(summary.total_savings)}</p>
        </button>

        <button
          onClick={() => setStatusFilter(statusFilter === 'more_expensive' ? 'all' : 'more_expensive')}
          className={`card p-4 sm:p-5 text-left transition-all ${
            statusFilter === 'more_expensive' ? 'ring-2 ring-rose-500/50' : ''
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <ArrowTrendingUpIcon className="w-4 h-4 text-rose-400" />
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">More Expensive</p>
          </div>
          <p className="text-3xl font-bold text-rose-400">{summary.more_expensive}</p>
          <p className="text-xs text-rose-400/70 mt-1">Overpay {formatPrice(summary.total_overpay)}</p>
        </button>

        <button
          onClick={() => setStatusFilter(statusFilter === 'same' ? 'all' : 'same')}
          className={`card p-4 sm:p-5 text-left transition-all ${
            statusFilter === 'same' ? 'ring-2 ring-slate-500/50' : ''
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <MinusCircleIcon className="w-4 h-4 text-slate-400" />
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Same Price</p>
          </div>
          <p className="text-3xl font-bold text-slate-300">{summary.same}</p>
          <p className="text-xs text-slate-500 mt-1">Matching competitor</p>
        </button>
      </div>

      {/* Highlights — biggest wins & biggest opportunities */}
      {(biggestWins.length > 0 || biggestOverpays.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* Biggest wins */}
          {biggestWins.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-700/50 bg-emerald-500/5">
                <h3 className="text-sm font-semibold text-emerald-400 flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4" />
                  Biggest Wins
                </h3>
              </div>
              <div className="divide-y divide-slate-700/30">
                {biggestWins.map((cat, i) => (
                  <Link
                    key={i}
                    to={`/match/${cat.match_id}`}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-slate-700/20 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-200 truncate">
                        {cat.home_team} v {cat.away_team}
                      </p>
                      <p className="text-[11px] text-slate-500">{cat.group_label}</p>
                    </div>
                    <span className="text-sm font-semibold text-emerald-400 shrink-0">
                      -{formatPrice(Math.abs(cat.diff))}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Biggest opportunities (price to review) */}
          {biggestOverpays.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-700/50 bg-rose-500/5">
                <h3 className="text-sm font-semibold text-rose-400 flex items-center gap-2">
                  <ExclamationTriangleIcon className="w-4 h-4" />
                  Pricing Opportunities
                </h3>
              </div>
              <div className="divide-y divide-slate-700/30">
                {biggestOverpays.map((cat, i) => (
                  <Link
                    key={i}
                    to={`/match/${cat.match_id}`}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-slate-700/20 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-200 truncate">
                        {cat.home_team} v {cat.away_team}
                      </p>
                      <p className="text-[11px] text-slate-500">
                        {cat.group_label} · vs {cat.cheapest_competitor_name}
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-rose-400 shrink-0">
                      +{formatPrice(Math.abs(cat.diff))}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* League filter chips */}
      {leagues.length > 1 && (
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2 scrollbar-none">
          <button
            onClick={() => setLeagueFilter(null)}
            className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              leagueFilter === null
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-800 text-slate-400 border border-slate-700/50 hover:bg-slate-700'
            }`}
          >
            All Leagues
          </button>
          {leagues.map((l) => (
            <button
              key={l.slug}
              onClick={() => setLeagueFilter(leagueFilter === l.slug ? null : l.slug)}
              className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                leagueFilter === l.slug
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-800 text-slate-400 border border-slate-700/50 hover:bg-slate-700'
              }`}
            >
              {l.name}
            </button>
          ))}
        </div>
      )}

      {/* Active filter indicator */}
      {activeFilters > 0 && (
        <div className="flex items-center gap-2 mb-4">
          <FunnelIcon className="w-4 h-4 text-slate-500" />
          <span className="text-sm text-slate-400">
            {statusFilter !== 'all' && (
              <span className="text-slate-200 font-medium mr-2">{statusFilter.replace('_', ' ')}</span>
            )}
            {leagueFilter && (
              <span className="text-slate-200 font-medium">
                {leagues.find((l) => l.slug === leagueFilter)?.name}
              </span>
            )}
          </span>
          <button
            onClick={() => { setStatusFilter('all'); setLeagueFilter(null); }}
            className="text-xs text-slate-500 hover:text-slate-300 underline ml-1"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Category breakdown table */}
      <div className="card overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-700/50 bg-slate-800/80">
          <h2 className="text-sm font-semibold text-slate-200">
            Category Breakdown
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {sorted.length} categor{sorted.length !== 1 ? 'ies' : 'y'} across{' '}
            {new Set(sorted.map((c) => c.match_id)).size} match
            {new Set(sorted.map((c) => c.match_id)).size !== 1 ? 'es' : ''}
          </p>
        </div>

        {/* Desktop table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700/50">
                <SortHeader label="Match" sortKey="match" current={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Category" sortKey="category" current={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Ticketed" sortKey="ticketed" current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                <SortHeader label="Competitor" sortKey="competitor" current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                <SortHeader label="Diff" sortKey="diff" current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                <SortHeader label="%" sortKey="pct" current={sortKey} dir={sortDir} onSort={handleSort} align="center" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/30">
              {sorted.map((cat, i) => (
                <CategoryRow key={i} cat={cat} />
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="sm:hidden divide-y divide-slate-700/30">
          {sorted.map((cat, i) => (
            <CategoryMobileRow key={i} cat={cat} />
          ))}
        </div>

        {sorted.length === 0 && (
          <div className="px-5 py-10 text-center">
            <p className="text-sm text-slate-500">No categories match the current filters.</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Sortable table header ──────────────────────────────────────────────── */

function SortHeader({
  label,
  sortKey: key,
  current,
  dir,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right' | 'center';
}) {
  const active = current === key;
  const alignCls = align === 'right' ? 'text-right justify-end' : align === 'center' ? 'text-center justify-center' : 'text-left';

  return (
    <th className={`px-5 py-2.5 ${alignCls}`}>
      <button
        onClick={() => onSort(key)}
        className={`inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wider transition-colors ${
          active ? 'text-emerald-400' : 'text-slate-500 hover:text-slate-300'
        }`}
      >
        {label}
        {active ? (
          dir === 'asc' ? <ChevronUpIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />
        ) : (
          <ArrowsUpDownIcon className="w-3 h-3 opacity-40" />
        )}
      </button>
    </th>
  );
}

/* ─── Table row components ───────────────────────────────────────────────── */

function CategoryRow({ cat }: { cat: InsightsCategory }) {
  return (
    <tr className="hover:bg-slate-700/20 transition-colors">
      <td className="px-5 py-3">
        <Link
          to={`/match/${cat.match_id}`}
          className="text-slate-300 hover:text-emerald-400 transition-colors"
        >
          {cat.home_team} v {cat.away_team}
        </Link>
        <p className="text-[11px] text-slate-500">
          {formatDateShort(cat.match_date)} {cat.league_name && `· ${cat.league_name}`}
        </p>
      </td>
      <td className="px-5 py-3 text-slate-400">{cat.group_label}</td>
      <td className="px-5 py-3 text-right font-medium text-sky-400">{formatPrice(cat.ticketed_price)}</td>
      <td className="px-5 py-3 text-right">
        <span className="text-slate-200">{formatPrice(cat.cheapest_competitor)}</span>
        <span className="text-[11px] text-slate-500 ml-1">{cat.cheapest_competitor_name}</span>
      </td>
      <td className="px-5 py-3 text-right">
        {cat.status === 'cheaper' ? (
          <span className="inline-flex items-center gap-1 text-emerald-400 font-medium">
            <ArrowTrendingDownIcon className="w-3.5 h-3.5" />
            {formatPrice(Math.abs(cat.diff))}
          </span>
        ) : cat.status === 'more_expensive' ? (
          <span className="inline-flex items-center gap-1 text-rose-400 font-medium">
            <ArrowTrendingUpIcon className="w-3.5 h-3.5" />
            +{formatPrice(Math.abs(cat.diff))}
          </span>
        ) : (
          <span className="text-slate-500">—</span>
        )}
      </td>
      <td className="px-5 py-3 text-center">
        {cat.status === 'cheaper' && (
          <span className="badge bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 text-[10px]">
            {Math.abs(cat.pct).toFixed(0)}% cheaper
          </span>
        )}
        {cat.status === 'more_expensive' && (
          <span className="badge bg-rose-500/15 text-rose-400 border border-rose-500/20 text-[10px]">
            {Math.abs(cat.pct).toFixed(0)}% more
          </span>
        )}
        {cat.status === 'same' && (
          <span className="badge bg-slate-700/50 text-slate-400 text-[10px]">Same</span>
        )}
      </td>
    </tr>
  );
}

function CategoryMobileRow({ cat }: { cat: InsightsCategory }) {
  return (
    <Link to={`/match/${cat.match_id}`} className="block px-4 py-3 hover:bg-slate-700/20 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-200 truncate">
            {cat.home_team} v {cat.away_team}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            {cat.group_label} · {formatDateShort(cat.match_date)}
            {cat.league_name && ` · ${cat.league_name}`}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-medium text-sky-400">{formatPrice(cat.ticketed_price)}</p>
          {cat.status === 'cheaper' ? (
            <p className="text-[11px] text-emerald-400">-{formatPrice(Math.abs(cat.diff))}</p>
          ) : cat.status === 'more_expensive' ? (
            <p className="text-[11px] text-rose-400">+{formatPrice(Math.abs(cat.diff))}</p>
          ) : (
            <p className="text-[11px] text-slate-500">Same</p>
          )}
        </div>
      </div>
    </Link>
  );
}
